import React from "react";
import { motion } from "framer-motion";

const TILES = [
  {
    label: "Brand Commercials",
    desc: "Product hero shots, brand spots, retail ads.",
    url: "https://static.prod-images.emergentagent.com/jobs/2ed9bb58-39cb-4164-b754-f91a45d29180/images/e644feaa3023537a3aef1e3453b9323a6cd28ec8d253f2466a666af5ba93531d.png",
    ratio: "vertical",
    span: "md:row-span-2",
  },
  {
    label: "Music Promos",
    desc: "Single rollouts, video snippets, merch drops.",
    url: "https://static.prod-images.emergentagent.com/jobs/2ed9bb58-39cb-4164-b754-f91a45d29180/images/69b5aa7ae503badf0a8c8e6182af250b80eddfbe141c3d39c3fe5d3672edd63a.png",
    ratio: "vertical",
    span: "md:row-span-2",
  },
  {
    label: "Product Ads",
    desc: "Luxury, lifestyle, e-commerce creatives.",
    url: "https://static.prod-images.emergentagent.com/jobs/2ed9bb58-39cb-4164-b754-f91a45d29180/images/53830624e894bdebe4e1eadaed27f342809342b23094384780ea79b3bea3cd02.png",
    ratio: "square",
  },
  {
    label: "Social Creatives",
    desc: "TikTok, Reels, Shorts — vertical & square ready.",
    url: "https://static.prod-images.emergentagent.com/jobs/2ed9bb58-39cb-4164-b754-f91a45d29180/images/93fe8fb077334afc57dd7e14eedc3e3f492c7f6f67c0308c0bc7775d4bbe5eda.png",
    ratio: "square",
  },
];

export default function UseCases() {
  return (
    <section
      id="use-cases"
      data-testid="use-cases-section"
      className="relative bg-[#080808] py-24 md:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
              Sample Outputs
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#F4F4F0] leading-[1.02] max-w-3xl">
              Made for the work<br />that pays the bills.
            </h2>
          </div>
          <p className="md:max-w-sm text-[#A0A0A0]">
            Four lanes. One platform. Every output engineered for promotion, conversion and brand quality — not novelty.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {TILES.map((tile, i) => (
            <motion.figure
              key={tile.label}
              data-testid={`use-case-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className={`relative group overflow-hidden border border-white/10 bg-[#121212] cursor-pointer ${
                tile.ratio === "vertical"
                  ? "col-span-1 md:col-span-1 aspect-[9/16]"
                  : "col-span-1 md:col-span-1 aspect-square"
              }`}
            >
              {/* Continuous ken-burns drift gives the still a 'video' feel */}
              <motion.img
                src={tile.url}
                alt={tile.label}
                loading="lazy"
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 8 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
                whileHover={{ scale: 1.12, transition: { duration: 1.2 } }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/20 to-transparent opacity-90" />

              {/* Play overlay on hover */}
              <motion.div
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <motion.div
                  className="w-14 h-14 rounded-full bg-[#D4AF37]/90 text-[#080808] flex items-center justify-center shadow-2xl"
                  animate={{ scale: [1, 1.12, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                >
                  <svg className="w-6 h-6 ml-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </motion.div>
              </motion.div>

              {/* Subtle scan-line shimmer */}
              <motion.div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 pointer-events-none"
                animate={{ y: ["-100%", "100%"] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
              />

              <figcaption className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">
                  {tile.ratio === "vertical" ? "9:16 · Vertical" : "1:1 · Square"}
                </div>
                <div className="font-display text-lg md:text-xl font-bold text-[#F4F4F0] tracking-tight">
                  {tile.label}
                </div>
                <div className="text-xs text-[#A0A0A0] mt-1 hidden md:block">{tile.desc}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
